/**
 * useCmsMagazines — published magazines, fresher than the bundle.
 *
 * Mirrors `useCmsProducts`: snapshot seeds state, live API replaces it on mount.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsMagazines, type CmsMagazineIndexEntry } from '@/content/cms/magazines.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

interface MagazinesResp {
  magazines?: CmsMagazineIndexEntry[]
}

export function useCmsMagazines(): CmsMagazineIndexEntry[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = cmsData?.magazines ?? cmsMagazines
  const [magazines, setMagazines] = useState<CmsMagazineIndexEntry[]>(initial)

  useEffect(() => {
    if (cmsData?.magazines) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/magazines?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<MagazinesResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.magazines?.length) return
        setMagazines(j.magazines)
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.magazines])

  return magazines
}
